import { isString, pickBy, isFinite, isArray } from 'lodash-es';

export class SearchParamsParseError extends Error {
  cause: unknown;

  constructor(cause: unknown) {
    super('ERR_SEARCH_PARAMS_FAILED_TO_PARSE');
    this.cause = cause;
  }
}

const parseToStr = (value: unknown) => {
  if (isString(value)) {
    return value;
  }

  /**
   * Check if value is a number except NaN, +-Infinity
   */
  if (isFinite(value)) {
    return String(value);
  }
};

const parseToNum = (value: unknown) => {
  if (isFinite(value) || isFinite(Number(value))) {
    return Number(value);
  }
};

/**
 * @description Arrays can be passed as values separated by comma
 *
 * It can be empty
 *
 * @example '1,2,3,4,10', ''
 */
const parseToArr = (value: unknown) => {
  if (isArray(value)) {
    return value;
  }

  if (value) {
    return parseToStr(value)
      ?.split(',')
      .filter(id => id.length > 0);
  }
};

const parseToNumArr = (value: unknown) => {
  return parseToArr(value)
    ?.map(item => parseToNum(item))
    .filter(item => item !== undefined) as number[] | undefined;
};

/**
 * @description Method to raise a validation error when a value
 * is undefined, but required
 *
 * `townFrom` is required, since search params cannot be built without it.
 * Whereas `additionalParams` are not required, since they are complementary
 */
const required = <T>(value: T) => {
  if (value !== undefined) {
    return value;
  }

  throw new SearchParamsParseError({ value });
};

const parseAdditionalParams = (
  unsafeAdditionalParams: Record<string, unknown>,
) => {
  const { hotels, stars, meal, isKgf, minPrice, maxPrice, sortBy, sortOrder } =
    unsafeAdditionalParams;

  return {
    hotels: parseToArr(hotels),
    stars: parseToArr(stars),
    meal: parseToArr(meal),
    isKgf: isKgf !== undefined ? Boolean(isKgf) : isKgf,
    minPrice: parseToNum(minPrice),
    maxPrice: parseToNum(maxPrice),
    sortBy: parseToStr(sortBy),
    sortOrder: parseToStr(sortOrder),
  };
};

const pickNonEmpty = (v?: string): v is string => {
  return v !== undefined && v.length > 0;
};

type RawSearchQueryParams = Record<string, string | undefined>;
const skipEmptyParams = (rawSearchParams: RawSearchQueryParams) => {
  return pickBy(rawSearchParams, pickNonEmpty);
};

export const spLib = {
  reqParseToNum: (val: unknown) => required(parseToNum(val)),
  reqParseToStr: (val: unknown) => required(parseToStr(val)),
  parseToArr,
  parseToNumArr,
  skipEmptyParams,
  parseAdditionalParams,
};
